import { useEffect, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Keyboard } from "@capacitor/keyboard";
import { isNativePlatform } from "@lib/platform";
import { initNativePushTapListener } from "@features/notifications/push/native-push-tap";
import { capacitorPreferencesStorage } from "@lib/platform/capacitor-storage";
import {
  bindSafeAreaResizeSync,
  configureNativeSafeArea,
  initSafeAreaEnvironment,
  syncSafeAreaAfterNativeLaunch,
} from "@lib/platform/safe-area";
import { bindKeyboardInsetSync } from "@lib/platform/keyboard-inset";
import { initNativeLinkHandler } from "@lib/platform/native-link-handler";
import { setStorageAdapter } from "@lib/platform/storage";
import { resolveWelcome } from "@features/welcome/welcome-decision";

async function initNativeShell(): Promise<void> {
  if (!isNativePlatform()) return;

  setStorageAdapter(capacitorPreferencesStorage);
  await configureNativeSafeArea();

  if (Capacitor.isPluginAvailable("StatusBar")) {
    try {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      await StatusBar.setStyle({
        style: prefersDark ? Style.Dark : Style.Light,
      });
    } catch {
      // ignore
    }
  }

  // While splash is still up, wait for WebView safe-area insets to settle.
  await syncSafeAreaAfterNativeLaunch();

  // Resolve the first-run welcome before revealing the app so the sign-in
  // screen never flashes ahead of the intro overlay.
  await resolveWelcome();

  try {
    await SplashScreen.hide();
  } catch {
    // ignore
  }

  if (Capacitor.isPluginAvailable("Keyboard")) {
    try {
      await Keyboard.setAccessoryBarVisible({ isVisible: true });
    } catch {
      // ignore
    }
  }
}

export function PlatformProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    initSafeAreaEnvironment();
    const unbindSafeArea = bindSafeAreaResizeSync();
    const unbindKeyboard = bindKeyboardInsetSync();

    void initNativeShell();
    void initNativePushTapListener();
    const removeLinkHandler = initNativeLinkHandler();

    let removeAppListener: (() => void) | undefined;
    if (isNativePlatform() && Capacitor.isPluginAvailable("App")) {
      void CapApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          void syncSafeAreaAfterNativeLaunch();
        }
      }).then((handle) => {
        removeAppListener = () => void handle.remove();
      });
    }

    return () => {
      unbindSafeArea();
      unbindKeyboard();
      removeLinkHandler();
      removeAppListener?.();
    };
  }, []);

  return children;
}
